import React, { useEffect, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { useIntl } from 'react-intl'
import { Chip } from '@arnsbogroup/react-voicearchive'
import messages from "./messages"

import Icon from 'components/Icon'
import OrderHistory from './components/OrderHistory'
import OrderDetails from './components/OrderDetails'
import OrderDeliveries from './components/OrderDeliveries'
import MemoryListBlock from 'components/MemoryListBlock'

import { Flex as Loading } from 'components/Loading'

const OrderDetailsPane = ({ order, onHide }) => {
  const { formatMessage } = useIntl()

  const [fetchedEvents, setFetchedEvents] = useState(null)
  const [lastSync, setLastSync] = useState(null)
  const [memories, setMemories] = useState(order.memories || [])
  const [activeTab, setActiveTab] = useState('details')

  useEffect(() => {
    setMemories(order.memories || [])
  }, [order.uid])

  useEffect(() => {
    setFetchedEvents(null)

    fetch(`/rest/v2/order/${order.uid}/events`)
      .then((response) => response.json())
      .then(({ data }) => setFetchedEvents(data))
      .catch((error) => window.alert(error))
  }, [order.uid, lastSync])

  const onRefetch = () => setLastSync(+Date.now())

  const onMemoryRecord = () => {
    fetch(`/rest/v2/order/${order.uid}/memories`)
      .then(r => r.json())
      .then(({ data }) => {
        if (data) {
          setMemories(data)
        }
      })
  }

  return (
    <div className="w-full h-full flex flex-col">
      <div className="shadow bg-gray-50 w-full px-1 md:px-2 xl:px-4 py-2 xl:py-3 text-xs font-medium text-gray-600 uppercase tracking-wider flex justify-between items-center">
        <span><FormattedMessage {...messages.details}/></span>
        <span>
          { onHide &&
            <Icon icon="times" onClick={onHide} className="cursor-pointer hover:text-gray-300"/>
          }
        </span>
      </div>

      <div>
        <OrderDetails fetchedEvents={fetchedEvents} order={order} />
      </div>

      <div className="border-b border-gray-200 px-2">
        <nav className="-mb-px flex text-gray-500 space-x-4">
          <button
            onClick={() => setActiveTab('details')}
            className={`whitespace-nowrap py-3 px-1 border-b-2 text-sm font-medium transition-colors ${
              activeTab === 'details'
                ? 'border-primary text-primary-darker'
                : 'border-transparent hover:border-gray-300 hover:text-gray-700'
            }`}
          >
            {formatMessage(messages.details)}
          </button>
          <button
            onClick={() => setActiveTab('pronunciations')}
            className={`whitespace-nowrap py-3 px-1 border-b-2 text-sm font-medium transition-colors flex items-center gap-1.5 ${
              activeTab === 'pronunciations'
                ? 'border-primary text-primary-darker'
                : 'border-transparent hover:border-gray-300 hover:text-gray-700'
            }`}
          >
            {formatMessage(messages.pronunciations)}
            {memories.length > 0 && (
              <Chip size="xs">{memories.length}</Chip>
            )}
          </button>
        </nav>
      </div>

      {activeTab === 'details' && (
        fetchedEvents ? (
          <div className='flex flex-1 overflow-hidden'>
            {fetchedEvents.some((event) => event?.type == "Delivery") &&
              <div className='flex-1 flex flex-col'>
                <OrderDeliveries fetchedEvents={fetchedEvents} onRefetch={onRefetch} order={order}/>
              </div>
            }
            <div className='flex-1 flex flex-col'>
              <OrderHistory fetchedEvents={fetchedEvents} onRefetch={onRefetch} order={order}/>
            </div>
          </div>
        ) : (
          <Loading />
        )
      )}

      {activeTab === 'pronunciations' && (
        <div className="p-4 overflow-y-auto">
          <MemoryListBlock memories={memories} onRecord={onMemoryRecord} />
          {memories.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-8">
              {formatMessage(messages.pronunciations)} — no entries yet.
            </p>
          )}
        </div>
      )}
    </div>
  )
}

export default OrderDetailsPane
